/**
 * Recursive node renderer. Switches on `node.type` and delegates to the
 * matching node editor component. Each editor manages its own
 * selection-aware toolbar via the shared editor context.
 */

import type { ContainerChild, LayoutNode } from '@news-cms/shared';
import { CarouselNodeEditor } from './carousel-node-editor';
import { ColumnNodeEditor } from './column-node-editor';
import { ContainerNodeEditor } from './container-node-editor';
import { LabelNodeEditor } from './label-node-editor';
import { RowNodeEditor } from './row-node-editor';
import { SlotNodeEditor } from './slot-node-editor';

export function NodeRenderer({ node, isRoot = false }: { node: LayoutNode; isRoot?: boolean }) {
  if (node.type === 'container') return <ContainerNodeEditor node={node} isRoot={isRoot} />;
  if (node.type === 'row') return <RowNodeEditor node={node} />;
  if (node.type === 'column') return <ColumnNodeEditor node={node} />;
  if (node.type === 'carousel') return <CarouselNodeEditor node={node} />;
  if (node.type === 'label') return <LabelNodeEditor node={node} />;
  return <SlotNodeEditor node={node} />;
}

/** Render the children of a Container or Column (anything except a bare
 *  Column). Shared by both since the child union is identical. */
export function ContainerChildrenRenderer({ children }: { children: ContainerChild[] }) {
  if (children.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400 dark:border-slate-700">
        ว่าง — กดปุ่ม "เพิ่ม" บน toolbar ด้านบนเพื่อเริ่ม
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {children.map((c) => (
        <NodeRenderer key={c.id} node={c} />
      ))}
    </div>
  );
}
